import React from 'react';
import type { ViewProps } from 'react-native';
import { ScrollView, StyleSheet } from 'react-native';
import {
  BodyText,
  Button,
  MobileHeader,
  Modal,
  theme,
  useResponsive,
  VStack,
} from '@sweetgreen/garnish';

import type { SubscriptionCreditCard } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateBack } from '../../navigation';

export const SweetpassPaymentMethodEditScreenContent = (
  props: SweetpassPaymentMethodEditScreenContentProps,
) => {
  const {
    testID,
    hasActiveSubscription,
    paymentMethod,
    isSettingActive,
    isRemoving,
    pressSetActiveHandler,
    pressRemoveHandler,
  } = props;

  const { t } = useLocalizationContext();

  const isBusy = isSettingActive || isRemoving;
  const isUseButtonDisabled = isBusy && !isSettingActive;
  const isRemoveButtonDisabled = isBusy && !isRemoving;

  return (
    <ScrollView testID={testID}>
      <SweetpassPaymentMethodEditScreenHeader {...props} />
      <Modal.BodyText testID={testID?.concat('.body')}>
        {t('account.sweetpass-membership.payment-methods.credit-card', {
          lastFour: paymentMethod?.lastFour,
          expiresAt: `${
            paymentMethod?.expirationMonth
          }/${paymentMethod?.expirationYear?.slice(-2)}`,
        })}
      </Modal.BodyText>
      <Modal.Footer testID={testID?.concat('.footer')}>
        {paymentMethod?.isActive ? (
          <BodyText
            testID={testID?.concat('.active-message')}
            style={styles.activeMessage}
            size={3}
          >
            {t(
              'account.sweetpass-membership.payment-methods.method-active-message',
            )}
          </BodyText>
        ) : (
          <VStack customSpacing={theme.spacing['4']}>
            {hasActiveSubscription ? (
              <Button
                testID={testID?.concat('.set-active-cta')}
                size="large"
                palette="primary"
                onPress={pressSetActiveHandler}
                isLoading={isSettingActive}
                disabled={isUseButtonDisabled}
              >
                {t(
                  'account.sweetpass-membership.payment-methods.set-active-cta.title',
                )}
              </Button>
            ) : null}
            <Button
              testID={testID?.concat('.remove-cta')}
              size="large"
              palette="caution"
              onPress={pressRemoveHandler}
              isLoading={isRemoving}
              disabled={isRemoveButtonDisabled}
            >
              {t(
                'account.sweetpass-membership.payment-methods.remove-cta.title',
              )}
            </Button>
          </VStack>
        )}
      </Modal.Footer>
    </ScrollView>
  );
};

const SweetpassPaymentMethodEditScreenHeader = ({
  testID,
  paymentMethod,
}: Pick<
  SweetpassPaymentMethodEditScreenContentProps,
  'testID' | 'paymentMethod'
>) => {
  const { currentBreakpoint } = useResponsive();
  const handleGoBack = useNavigateBack();

  if (currentBreakpoint.isXS) {
    return (
      <MobileHeader
        testID={testID?.concat('.headline')}
        title={paymentMethod?.cardType}
        style={styles.headerXS}
      />
    );
  }

  return (
    <Modal.Header testID={testID?.concat('.headline')} onClose={handleGoBack}>
      {paymentMethod?.cardType}
    </Modal.Header>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type PaymentMethod = NonNullable<SubscriptionCreditCard> | undefined;

type SweetpassPaymentMethodEditScreenContentProps = {
  readonly hasActiveSubscription: boolean;
  readonly paymentMethod: PaymentMethod;
  readonly isSettingActive: boolean;
  readonly isRemoving: boolean;
  readonly pressSetActiveHandler: () => void;
  readonly pressRemoveHandler: () => void;
} & Pick<ViewProps, 'testID'>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  activeMessage: {
    color: theme.colors.NEUTRAL_2,
  },
  headerXS: {
    marginHorizontal: theme.spacing['4'],
  },
});
